import { initSentry, Sentry } from "@/lib/sentry";
initSentry();

import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

function injectUmamiAnalytics() {
  if (typeof document === "undefined") return;

  const endpoint = (import.meta.env.VITE_ANALYTICS_ENDPOINT as string | undefined) ?? "";
  const websiteId = (import.meta.env.VITE_ANALYTICS_WEBSITE_ID as string | undefined) ?? "";

  if (!endpoint || !websiteId) return;

  const normalizedEndpoint = endpoint.replace(/\/+$/, "");
  if (!normalizedEndpoint) return;

  // Avoid injecting duplicates (e.g. HMR / reloads)
  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-website-id][src*="/umami"]'
  );
  if (existing) return;

  const script = document.createElement("script");
  script.defer = true;
  script.src = `${normalizedEndpoint}/umami`;
  script.dataset.websiteId = websiteId;
  document.head.appendChild(script);
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    if (!(error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG)) {
      Sentry.captureException(error, { tags: { source: "trpc-query" } });
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    if (!(error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG)) {
      Sentry.captureException(error, { tags: { source: "trpc-mutation" } });
    }
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

injectUmamiAnalytics();

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
